function parseJson(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function setPriceMarkup(node, compareAtPrice, price) {
  if (!node) return;

  node.textContent = '';
  if (compareAtPrice && compareAtPrice !== price) {
    const compareNode = document.createElement('s');
    compareNode.textContent = compareAtPrice;
    node.appendChild(compareNode);
    node.appendChild(document.createTextNode(' '));
  }
  node.appendChild(document.createTextNode(price || ''));
}

function initMiniSellingPlan(root) {
  if (!(root instanceof HTMLElement) || root.dataset.miniSellingPlanInitialized === 'true') return;
  root.dataset.miniSellingPlanInitialized = 'true';

  if (root.dataset.miniSellingPlanEnabled !== 'true') return;

  const panel = root.querySelector('[data-mini-selling-plan-panel]');
  const sellingPlanInput = root.querySelector('[data-mini-selling-plan-input]');
  const purchaseInputs = Array.from(root.querySelectorAll('[data-mini-selling-plan-purchase]'));
  const subscriptionInput = purchaseInputs.find((input) => input.value === 'subscription');
  const oneTimeInput = purchaseInputs.find((input) => input.value === 'onetime');
  const planSelect = root.querySelector('[data-mini-selling-plan-select]');
  const mapNode = root.querySelector('[data-mini-selling-plan-map]');
  const planMap = parseJson(mapNode?.textContent || '');
  const form = root.closest("form[action*='/cart/add']");
  const submitButton = root.querySelector("button[type='submit'][name='add']");
  const toggleButton = root.querySelector('[data-mini-selling-plan-toggle]');
  const variantInput = form?.querySelector("input[name='id'], select[name='id']");
  const variantOptions = Array.from(root.querySelectorAll('[data-mini-variant-option]'));
  const variantSelect = root.querySelector('[data-mini-variant-select]');
  const inlineToggle = root.querySelector('[data-mini-inline-toggle]');
  const inlineControl = root.querySelector('[data-mini-inline-control]');
  const inlineLabel = root.querySelector('[data-mini-inline-label]');
  const oneTimePriceEl = root.querySelector('[data-mini-onetime-price]');
  const subscriptionPriceEl = root.querySelector('[data-mini-subscription-price]');
  const addToCartTextEl = root.querySelector('.add-to-cart-text__content');
  const atcLabel = root.dataset.miniAtcLabel || '';
  const inlinePurchaseOptions = root.dataset.miniSellingPlanInline === 'true';
  const productId =
    root.dataset.miniProductId ||
    form.closest('product-form-component')?.dataset.productId ||
    '';

  if (!panel || !sellingPlanInput || !subscriptionInput || !oneTimeInput || !planSelect || !form || !submitButton) return;

  let hasPlansForVariant = true;
  let currentVariantState = null;
  let wantsSubscription = inlineToggle?.checked ?? true;
  let ignoreOutsidePointerDownUntil = 0;
  const desktopHoverQuery = window.matchMedia?.('(min-width: 750px) and (hover: hover) and (pointer: fine)');
  const supportsDesktopHover = () => desktopHoverQuery?.matches === true;

  const ensureChoice = () => {
    if (subscriptionInput.checked || oneTimeInput.checked) return;
    subscriptionInput.checked = true;
  };

  const collapsePanel = () => {
    panel.hidden = true;
    root.classList.remove('add-to-cart-with-plan--is-expanded');
  };

  const syncSellingPlan = () => {
    if (!hasPlansForVariant || oneTimeInput.checked) {
      sellingPlanInput.value = '';
      sellingPlanInput.disabled = true;
      return;
    }

    if (!planSelect.value && planSelect.options.length > 0) {
      planSelect.value = planSelect.options[0].value;
    }

    sellingPlanInput.value = planSelect.value || '';
    sellingPlanInput.disabled = !sellingPlanInput.value;
  };

  const syncAddToCartPrice = () => {
    if (!addToCartTextEl || !currentVariantState) return;

    const selectedPlan =
      currentVariantState.plans?.find((plan) => String(plan.id) === String(planSelect.value)) ||
      currentVariantState.plans?.[0] ||
      null;

    const activePrice = oneTimeInput.checked ? currentVariantState.one_time_price : selectedPlan?.price;
    if (!activePrice) return;

    addToCartTextEl.innerHTML = atcLabel ? `<span>${atcLabel}</span><span class="is-desktop-only">${activePrice}</span>` : activePrice;
  };

  const usesCompareAtAsStriked = () => {
    const scope = root.closest('.product-card, product-card, .product-details, .shopify-section, dialog') || document;
    return Boolean(
      scope.querySelector(`product-price[data-product-id="${productId}"][data-use-compare-at-as-striked="true"]`)
    );
  };

  const resolveWidgetCompareAt = (fallbackCompareAt) => {
    if (usesCompareAtAsStriked()) {
      return currentVariantState?.shopify_compare_at_price || '';
    }

    return fallbackCompareAt || '';
  };

  const syncPriceUi = () => {
    if (!currentVariantState) return;

    const selectedPlan =
      currentVariantState.plans?.find((plan) => String(plan.id) === String(planSelect.value)) ||
      currentVariantState.plans?.[0] ||
      null;
    const oneTimeCompareAt = resolveWidgetCompareAt(currentVariantState.one_time_compare_at_price);
    const subscriptionCompareAt = resolveWidgetCompareAt(selectedPlan?.compare_at_price);

    setPriceMarkup(oneTimePriceEl, oneTimeCompareAt, currentVariantState.one_time_price);
    setPriceMarkup(subscriptionPriceEl, subscriptionCompareAt, selectedPlan?.price || '');
    syncAddToCartPrice();
    const activePrice = oneTimeInput.checked ? currentVariantState.one_time_price : selectedPlan?.price || '';
    const activeCompareAt = oneTimeInput.checked ? oneTimeCompareAt : subscriptionCompareAt;

    root.dispatchEvent(
      new CustomEvent('mini-selling-plan:price-change', {
        bubbles: true,
        detail: {
          productId: String(productId || ''),
          price: activePrice,
          compareAtPrice: activeCompareAt,
          sourceRoot: root,
        },
      })
    );
  };

  const renderPlansForVariant = () => {
    if (!planMap || !variantInput) {
      hasPlansForVariant = planSelect.options.length > 0;
      ensureChoice();
      syncSellingPlan();
      return;
    }

    const variantState = planMap[String(variantInput.value)] || null;
    const plans = variantState?.plans || [];
    currentVariantState = variantState;

    hasPlansForVariant = plans.length > 0;
    if (!hasPlansForVariant) {
      if (inlinePurchaseOptions) {
        if (inlineControl) inlineControl.hidden = true;
        if (inlineToggle) inlineToggle.checked = false;
        oneTimeInput.checked = true;
        subscriptionInput.checked = false;
        syncPriceUi();
      } else {
        currentVariantState = null;
        collapsePanel();
      }
      sellingPlanInput.value = '';
      sellingPlanInput.disabled = true;
      return;
    }

    const previousValue = planSelect.value;
    const existingValues = Array.from(planSelect.options).map((option) => option.value);
    const nextValues = plans.map((plan) => String(plan.id));
    const optionsChanged =
      existingValues.length !== nextValues.length ||
      existingValues.some((value, index) => value !== nextValues[index]);

    if (optionsChanged) {
      planSelect.textContent = '';
      plans.forEach((plan) => {
        const option = document.createElement('option');
        option.value = String(plan.id);
        option.textContent = plan.name;
        planSelect.appendChild(option);
      });
    }

    if (plans.some((plan) => String(plan.id) === String(previousValue))) {
      planSelect.value = previousValue;
    } else {
      planSelect.value = String(plans[0].id);
    }

    if (inlinePurchaseOptions) {
      if (inlineControl) inlineControl.hidden = false;
      if (inlineToggle) inlineToggle.checked = wantsSubscription;
      inlineControl?.classList.toggle('is-one-time', !wantsSubscription);
      if (inlineLabel) {
        inlineLabel.textContent = wantsSubscription
          ? inlineLabel.dataset.subscriptionLabel
          : inlineLabel.dataset.oneTimeLabel;
      }
      subscriptionInput.checked = wantsSubscription;
      oneTimeInput.checked = !wantsSubscription;
    }
    ensureChoice();
    syncPriceUi();
    syncSellingPlan();
    if (inlinePurchaseOptions) revealPanel();
  };

  const revealPanel = () => {
    if (!hasPlansForVariant) return;
    panel.hidden = false;
    root.classList.add('add-to-cart-with-plan--is-expanded');
  };

  const clearAtcAnimationState = () => {
    submitButton.classList.remove('atc-added');
  };

  const maybeInterceptFirstClick = (event) => {
    if (inlinePurchaseOptions) return;
    if (!hasPlansForVariant || !panel.hidden) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
    revealPanel();
    clearAtcAnimationState();
  };

  const captureSubmitButtonClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest("button[type='submit'][name='add']")) return;
    if (!root.contains(target)) return;
    maybeInterceptFirstClick(event);
  };

  const handleSubmit = (event) => {
    if (!inlinePurchaseOptions && hasPlansForVariant && panel.hidden) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
      revealPanel();
      clearAtcAnimationState();
      return;
    }

    renderPlansForVariant();
    ensureChoice();
    syncSellingPlan();
  };

  const handleToggleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    if (panel.hidden) {
      revealPanel();
    } else {
      collapsePanel();
    }
    clearAtcAnimationState();
  };

  const handleToggleMouseOver = () => {
    if (inlinePurchaseOptions) return;
    if (!supportsDesktopHover()) return;
    revealPanel();
    clearAtcAnimationState();
  };

  const handleRootMouseOut = (event) => {
    if (inlinePurchaseOptions) return;
    if (!supportsDesktopHover()) return;
    if (event.relatedTarget instanceof Node && root.contains(event.relatedTarget)) return;
    collapsePanel();
  };

  const handlePanelPointerDown = (event) => {
    event.stopPropagation();
  };

  const activateOutsideCloseLock = () => {
    ignoreOutsidePointerDownUntil = Date.now() + 1200;
  };

  const handleOutsidePointerDown = (event) => {
    if (inlinePurchaseOptions) return;
    if (!(event.target instanceof Node)) return;
    if (panel.hidden || !root.classList.contains('add-to-cart-with-plan--is-expanded')) return;
    if (Date.now() < ignoreOutsidePointerDownUntil) return;
    if (document.activeElement === planSelect) return;
    if (root.contains(event.target)) return;
    collapsePanel();
  };

  submitButton.addEventListener('click', captureSubmitButtonClick, true);
  form.addEventListener(
    'submit',
    handleSubmit,
    true
  );
  toggleButton?.addEventListener('click', handleToggleClick, true);
  (toggleButton || submitButton).addEventListener('mouseover', handleToggleMouseOver, true);
  root.addEventListener('mouseout', handleRootMouseOut, true);
  panel.addEventListener('pointerdown', handlePanelPointerDown, true);
  planSelect.addEventListener('pointerdown', handlePanelPointerDown, true);
  planSelect.addEventListener('touchstart', handlePanelPointerDown, { capture: true, passive: true });
  planSelect.addEventListener('pointerdown', activateOutsideCloseLock, true);
  planSelect.addEventListener('touchstart', activateOutsideCloseLock, { capture: true, passive: true });
  planSelect.addEventListener('focus', activateOutsideCloseLock, true);
  planSelect.addEventListener('click', activateOutsideCloseLock, true);
  planSelect.addEventListener('change', activateOutsideCloseLock, true);
  planSelect.addEventListener('blur', () => {
    ignoreOutsidePointerDownUntil = Date.now() + 200;
  });
  document.addEventListener('pointerdown', handleOutsidePointerDown, true);

  purchaseInputs.forEach((input) => {
    input.addEventListener('change', () => {
      ensureChoice();
      syncPriceUi();
      syncSellingPlan();
    });
  });

  inlineToggle?.addEventListener('change', () => {
    wantsSubscription = inlineToggle.checked;
    subscriptionInput.checked = wantsSubscription;
    oneTimeInput.checked = !wantsSubscription;
    inlineControl?.classList.toggle('is-one-time', !wantsSubscription);
    if (inlineLabel) {
      inlineLabel.textContent = wantsSubscription
        ? inlineLabel.dataset.subscriptionLabel
        : inlineLabel.dataset.oneTimeLabel;
    }
    syncPriceUi();
    syncSellingPlan();
  });

  planSelect.addEventListener('change', () => {
    syncPriceUi();
    syncSellingPlan();
  });

  variantInput?.addEventListener('change', renderPlansForVariant);
  variantInput?.addEventListener('input', renderPlansForVariant);

  variantSelect?.addEventListener('change', () => {
    if (!variantInput) return;
    variantInput.value = variantSelect.value;
    variantInput.dispatchEvent(new Event('input', { bubbles: true }));
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  });

  variantOptions.forEach((option) => {
    option.addEventListener('change', () => {
      if (!variantInput || !option.checked) return;
      variantInput.value = option.value;
      variantInput.dispatchEvent(new Event('input', { bubbles: true }));
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      renderPlansForVariant();
    });
  });

  document.addEventListener('variant:update', (event) => {
    const targetProductForm = root.closest('product-form-component');
    if (!targetProductForm) return;

    const eventProductId = String(event?.detail?.data?.productId || '');
    if (eventProductId !== String(targetProductForm.dataset.productId || '')) return;

    if (event?.detail?.resource?.id && variantInput) {
      variantInput.value = String(event.detail.resource.id);
    }

    const currentVariantId = String(variantInput?.value || '');
    if (variantSelect) variantSelect.value = currentVariantId;
    variantOptions.forEach((option) => {
      option.checked = String(option.value) === currentVariantId;
    });
    renderPlansForVariant();
  });

  if (inlinePurchaseOptions) revealPanel();
  else collapsePanel();
  ensureChoice();
  const currentVariantId = String(variantInput?.value || '');
  if (variantSelect) variantSelect.value = currentVariantId;
  variantOptions.forEach((option) => {
    option.checked = String(option.value) === currentVariantId;
  });
  renderPlansForVariant();
  window.setTimeout(() => syncPriceUi(), 0);
  window.setTimeout(() => syncPriceUi(), 150);
}

function initAllMiniSellingPlans() {
  document.querySelectorAll('[data-mini-selling-plan]').forEach((node) => initMiniSellingPlan(node));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initAllMiniSellingPlans();
    const observer = new MutationObserver(initAllMiniSellingPlans);
    observer.observe(document.body, { childList: true, subtree: true });
  });
} else {
  initAllMiniSellingPlans();
  const observer = new MutationObserver(initAllMiniSellingPlans);
  observer.observe(document.body, { childList: true, subtree: true });
}
